import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Sublayer from '@arcgis/core/layers/support/Sublayer';
import { finalize, map, Subscription } from 'rxjs';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { ToastService } from 'app/shared/services/toast.service';
import { IdentifyService } from '../../identify.service';

interface AttachmentItemLike {
  type: 'image' | 'default';
  url: string;
  fileName: string;
  name: string;
  ext: string;
  contentType: string;
}

@Component({
  selector: 'app-attachments',
  templateUrl: 'attachments.component.html',
  styleUrls: ['attachments.component.scss'],
})
export class AttachmentsComponent implements OnChanges, OnDestroy {
  @Input()
  layer: __esri.FeatureLayer | __esri.Sublayer;
  @Input()
  objectId: number;

  items: AttachmentItemLike[];

  private featureLayer: __esri.FeatureLayer;
  private featureLayerSubscription: Subscription;
  private attachmentsSubscription: Subscription;

  readonly loadingController = new LoadingController();

  constructor(
    private identifyService: IdentifyService,
    private toastService: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.layer) {
      this.featureLayerSubscription?.unsubscribe();

      let featureLayer: __esri.FeatureLayer;
      if (this.layer instanceof Sublayer) {
        featureLayer = new FeatureLayer({ url: this.layer.url });
        this.featureLayerSubscription = new Subscription(() => {
          featureLayer.destroy();
        });
      } else {
        featureLayer = this.layer;
      }

      this.featureLayer = featureLayer;
    }

    if (changes.layer || changes.objectId) {
      this.handleAttachments();
    }
  }

  ngOnDestroy(): void {
    this.attachmentsSubscription?.unsubscribe();
    this.featureLayerSubscription?.unsubscribe();
  }

  private handleAttachments() {
    this.attachmentsSubscription?.unsubscribe();
    if (!this.layer || !this.objectId) {
      this.items = null;
      return;
    }

    this.loadingController.show();
    this.attachmentsSubscription = this.identifyService
      .getAttachments(this.featureLayer, this.objectId)
      .pipe(
        finalize(() => {
          this.loadingController.hide();
        }),
        map((items) => {
          return items?.map<AttachmentItemLike>((x) => {
            const { contentType } = x;

            const type = contentType?.startsWith('image/')
              ? 'image'
              : 'default';

            const nameArr = x.name.split('.');
            const name = nameArr[0];
            const ext = nameArr[1];

            return {
              type,
              url: x.url,
              fileName: x.name,
              name,
              ext,
              contentType,
            };
          });
        })
      )
      .subscribe({
        next: (items) => {
          this.items = items;
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(`Não foi possível carregar os anexos!`);
        },
      });
  }

  resolveItemIcon(contentType: string, ext?: string) {
    return resolveIcon(contentType, ext);
  }
}

const fileIconMap = new Map<string, string>();

function registerIcon(keyOrKeys: string | string[], icon: string) {
  if (!Array.isArray(keyOrKeys)) {
    keyOrKeys = [keyOrKeys];
  }

  keyOrKeys.forEach((key) => {
    fileIconMap.set(key, icon);
  });
}

registerIcon(
  [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'xls',
    'xlsx',
  ],
  'xls_P&B.svg'
);
registerIcon(
  [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'doc',
    'docx',
  ],
  'doc_P&B.svg'
);
registerIcon(['text/csv', 'csv'], 'file-csv-solid.svg');
registerIcon(['text/plain', 'txt'], 'txt_P&B.svg');
registerIcon(['application/pdf', 'pdf'], 'pdf_P&B.svg');
registerIcon(['video/mp4', 'mp4'], 'mp4_P&b.svg');

function resolveIcon(contentType: string, ext?: string) {
  return (
    fileIconMap.get(contentType?.toLowerCase()) ??
    fileIconMap.get(ext?.toLowerCase()) ??
    'file_P&B.svg'
  );
}
